import { useUpdateEffect } from 'react-use';

import { useFormController } from '@dreryk/react-common';
import {
   componentsTranslationKeys,
   Form,
   FormRouteLeavingPrompt,
   StyledFlex,
   StyledMargin,
} from '@dreryk/react-components';

import {
   AdvancePaymentsSettingsFormBean,
   AdvancePaymentsSettingsFormController,
   AdvancePaymentsSettingsFormInitializer,
} from './AdvancePaymentsSettingsForm.types';
import { AutomaticRefundField } from './fields/AutomaticRefundField';
import { RefundTypeField } from './fields/RefundTypeField';
import { TimeBasedRefundInteractiveTable } from './fields/timeBasedRefundInteractiveTable/TimeBasedRefundInteractiveTable';
import { refundTypeVisitor } from './AdvancePaymentsSettingsForm.utils';
import { FULL_REFUND_THRESHOLD_IN_HOURS_DEFAULT_VALUE } from './fields/FullRefundThresholdInHoursField';
import { addEmptyRow } from './fields/timeBasedRefundInteractiveTable/TimeBasedRefundInteractiveTable.utils';

type AdvancePaymentsSettingsFormRenderProps = Pick<
   AdvancePaymentsSettingsFormController,
   'handleSubmit' | 'markPristine'
>;

type AdvancePaymentsSettingsFormProps = {
   children: (renderProps: AdvancePaymentsSettingsFormRenderProps) => JSX.Element;
   initializer: AdvancePaymentsSettingsFormInitializer;
};

export const AdvancePaymentsSettingsForm = (props: AdvancePaymentsSettingsFormProps): JSX.Element => {
   const {
      handleSubmit,
      register,
      watch,
      setValue,
      setError,
      formState,
      markPristine,
      trigger,
   } = useFormController<AdvancePaymentsSettingsFormBean>(props.initializer());

   const bean = watch();

   useUpdateEffect(() => {
      if (bean.automaticRefundEnabled) {
         setValue('refundType', 'FULL_REFUND');
         setValue('fullRefundThresholdInHours', FULL_REFUND_THRESHOLD_IN_HOURS_DEFAULT_VALUE);
      }
   }, [bean.automaticRefundEnabled]);

   useUpdateEffect(() => {
      if (bean.refundType === 'TIME_BASED_REFUND') {
         setError('fullRefundThresholdInHours', false);

         if (bean.timeBasedRefund?.length === 0 || !Array.isArray(bean.timeBasedRefund)) {
            setValue('timeBasedRefund', [addEmptyRow()]);
         }
      }

      if (bean.refundType === 'FULL_REFUND') {
         trigger('fullRefundThresholdInHours');
      }
   }, [bean.refundType]);

   return (
      <StyledFlex gap={12} direction={'column'}>
         <Form testId={'advancePaymentsSettingsForm'} autoComplete={false} noValidate>
            <StyledFlex gap={12} direction={'column'}>
               <AutomaticRefundField register={register} />

               {bean.automaticRefundEnabled ? (
                  <StyledMargin left={24}>
                     <StyledFlex gap={12} direction={'column'}>
                        <RefundTypeField register={register} watch={watch} />

                        {refundTypeVisitor(bean)({
                           visitTimeBasedRefund: () => (
                              <StyledMargin left={24}>
                                 <TimeBasedRefundInteractiveTable
                                    register={register}
                                    watch={watch}
                                    setError={setError}
                                 />
                              </StyledMargin>
                           ),
                           visitFullRefund: () => null,
                           visitWithoutAutomaticRefund: () => null,
                        })}
                     </StyledFlex>
                  </StyledMargin>
               ) : null}
            </StyledFlex>

            <FormRouteLeavingPrompt formState={formState} message={componentsTranslationKeys.formLeavingConfirmation} />
         </Form>

         {props.children({ handleSubmit: handleSubmit, markPristine: markPristine })}
      </StyledFlex>
   );
};
